import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "reactstrap";
import { Oval as Loader } from "react-loader-spinner";
import CCBillModal from "../../Pages/CCbill/CCBillModal";
import apiCache from "../../../shared/utils/apiCache";
import {
  closeAlert,
  showPaymentProcessing,
  confirmDialog,
} from "../../../shared/utils/swalHandler";
import {
  showCallCreditsAddedPushToast,
  showPurchaseFailedPushToast,
} from "../../../shared/pusNotification/useManualToasts";
import {
  fetchCreditListAction,
  fetchWalletBalanceAction,
  purchaseCredits,
  cancelCreditPurchase
} from "../../../shared/actions/creditAction";
import { charge } from "../../../shared/actions/ccbillAction";
import { PAYMENT_INFO } from "../../../shared/constants/common";
import {
  LOW_BALANCE_CRITICAL_WARNING_MINUTES,
  LOW_BALANCE_WARNING_MINUTES,
} from "../../../views/Pages/Message/call/utils/callFormatUtils";
import {
  hasValidValue,
  getFormFieldValue,
  buildPassThroughInfo,
  extractPurchaseDetails,
  buildPurchasePayload,
  getVatAndServiceFeeAppliedText,
} from "../../../shared/utils/paymentUtil";
import { setPaymentFlowActive } from "../../../shared/utils/config/paymentFlowGuard";

const getItemId = (item) => item.id ?? item._id;
const getCredits = (item) => item.credits ?? item.creditAmount ?? item.amount ?? 0;
const getPrice = (item) => item.price ?? item.amount ?? 0;
const PURCHASE_PAYMENT_FAILED_TITLE = "Payment Failed";

const PurchaseCreditsModal = ({
  isOpen,
  onClose,
  variant = "default",
  lowCreditMinutesRemaining = null,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const [selectedCreditOption, setSelectedCreditOption] = useState(null);
  const { creditList = [], loading, error } = useSelector((state) => state.credit);
  const isLowCreditCallout = variant === "lowCredit";

  const [ccBillModalOpen, setCcBillModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null);
  const [purchaseTransactionId, setPurchaseTransactionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchasePayload, setPurchasePayload] = useState(null);

  // Block signed-URL refresh (page reload) while purchase credits is in progress
  useEffect(() => {
    setPaymentFlowActive(isOpen);
    return () => setPaymentFlowActive(false);
  }, [isOpen]);

  // Restore body/html scroll when modal closes so page scroll works again.
  // Reactstrap Modal sets overflow hidden on body; ensure we clear it on close and on unmount.
  useEffect(() => {
    const restoreScroll = () => {
      const body = document.body;
      const html = document.documentElement;
      if (body) {
        body.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
      }
      if (html) {
        html.style.overflow = '';
      }
    };
    if (!isOpen) {
      restoreScroll();
    }
    return restoreScroll;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCreditListAction());
    }
  }, [isOpen, dispatch]);

  /** Low-credit call UI: default to first package so "Continue Call" works immediately. */
  useEffect(() => {
    if (!isOpen || !isLowCreditCallout || loading || !creditList.length) return;
    setSelectedCreditOption((prev) => prev ?? getItemId(creditList[0]));
  }, [isOpen, isLowCreditCallout, loading, creditList]);

  const selectedItem = useMemo(
    () => creditList.find((item) => getItemId(item) === selectedCreditOption),
    [creditList, selectedCreditOption]
  );
  const selectedCreditsAmount = useMemo(
    () => (selectedItem ? getCredits(selectedItem) : 0),
    [selectedItem]
  );

  const lowCreditMinutesLabel = useMemo(() => {
    const minutes = Number(lowCreditMinutesRemaining);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return String(LOW_BALANCE_WARNING_MINUTES);
    }
    const rounded = Math.max(1, Math.round(minutes));
    if (minutes <= LOW_BALANCE_CRITICAL_WARNING_MINUTES) {
      return String(rounded);
    }
    return String(Math.min(rounded, LOW_BALANCE_WARNING_MINUTES));
  }, [lowCreditMinutesRemaining]);

  const lowCreditSubtitle = useMemo(() => {
    const minutes = Number(lowCreditMinutesRemaining);
    const isCritical =
      Number.isFinite(minutes) && minutes <= LOW_BALANCE_CRITICAL_WARNING_MINUTES;
    const timePhrase = isCritical
      ? `${lowCreditMinutesLabel} minute${lowCreditMinutesLabel === "1" ? "" : "s"}`
      : `${lowCreditMinutesLabel} minutes`;
    return `You have ${timePhrase} worth of credits left before the call ends. Top up to continue!`;
  }, [lowCreditMinutesLabel, lowCreditMinutesRemaining]);

  useEffect(() => {
    if (!selectedItem) {
      setPurchasePayload(null);
      return;
    }
    const price = getPrice(selectedItem);
    let cancelled = false;
    buildPurchasePayload(price, 0, user).then((payload) => {
      if (!cancelled) setPurchasePayload(payload);
    });
    return () => { cancelled = true; };
  }, [selectedCreditOption, selectedItem, user]);

  const resetPurchaseState = useCallback(() => {
    setPaymentForm(null);
    setPurchaseTransactionId(null);
    setIsProcessing(false);
  }, []);

  const showPurchasePaymentError = useCallback((message) => {
    showPurchaseFailedPushToast(
      PURCHASE_PAYMENT_FAILED_TITLE,
      message || "Payment failed. Please try again.",
      "credits"
    );
  }, []);

  const fetchSavedPaymentToken = useCallback(async () => {
    const token = await apiCache.getPaymentTokenId();
    return hasValidValue(token) ? token : null;
  }, []);

  const handlePurchaseSuccess = useCallback(async () => {
    setCcBillModalOpen(false);
    resetPurchaseState();
    await dispatch(fetchWalletBalanceAction());
    onClose?.();
    closeAlert();
  }, [dispatch, onClose, resetPurchaseState]);

  const closeCCBillModal = useCallback(async () => {
    const pid = purchaseTransactionId || (paymentForm && getFormFieldValue(paymentForm, "c_transaction_id"));
    if (pid) {
      try {
        await cancelCreditPurchase(pid, false);
      } catch (_ignored) { }
    }
    setCcBillModalOpen(false);
    resetPurchaseState();
  }, [purchaseTransactionId, paymentForm, resetPurchaseState]);

  const cancelCCBillModal = useCallback(async () => {
    const pid = purchaseTransactionId || (paymentForm && getFormFieldValue(paymentForm, "c_transaction_id"));
    if (pid) {
      try {
        await cancelCreditPurchase(pid, true);
      } catch (_ignored) { }
    }
    setCcBillModalOpen(false);
    resetPurchaseState();
  }, [purchaseTransactionId, paymentForm, resetPurchaseState]);

  const handleCCBillModalSuccess = useCallback(async () => {
    setCcBillModalOpen(false);
    closeAlert();
    showCallCreditsAddedPushToast(selectedCreditsAmount);
    await handlePurchaseSuccess();
  }, [handlePurchaseSuccess, selectedCreditsAmount]);

  const chargeWithExistingToken = useCallback(
    async (transactionId, chargeData) => {
      try {
        setIsProcessing(true);
        showPaymentProcessing({});
        const res = await charge(chargeData);
        if (res.success) {
          closeAlert();
          showCallCreditsAddedPushToast(selectedCreditsAmount);
          await handlePurchaseSuccess();
          return true;
        }
        closeAlert();
        if (transactionId) await cancelCreditPurchase(transactionId);
        showPurchasePaymentError(res.error || "Payment failed. Please try again.");
        return false;
      } catch (err) {
        closeAlert();
        if (transactionId) await cancelCreditPurchase(transactionId);
        showPurchasePaymentError(
          err?.response?.data?.message || err?.message || "Payment failed. Please try again."
        );
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [handlePurchaseSuccess, selectedCreditsAmount, showPurchasePaymentError, showPaymentProcessing]
  );

  const handleConfirmPurchase = useCallback(async () => {
    if (isProcessing || !selectedCreditOption || !purchasePayload) {
      if (!selectedCreditOption) {
        showPurchasePaymentError("Please select a credit option.");
      } else if (!purchasePayload) {
        showPurchasePaymentError("Invalid price information.");
      }
      return;
    }

    try {
      setIsProcessing(true);
      showPaymentProcessing({});

      const existingToken = await fetchSavedPaymentToken();
      const payloadWithToken =
        existingToken != null && existingToken !== ""
          ? { ...purchasePayload, paymentTokenId: existingToken }
          : purchasePayload;

      const res = await purchaseCredits(selectedCreditOption, payloadWithToken);
      if (!res.success) {
        closeAlert();
        showPurchasePaymentError(res.error || "Something went wrong. Please try again.");
        return;
      }

      const responseData = res.data?.data ?? res.data;
      const { purchaseId, nextPaymentForm, paymentGateway, paymentOneClick } = extractPurchaseDetails(responseData);

      if (paymentOneClick === 1) {
        closeAlert();
        showCallCreditsAddedPushToast(selectedCreditsAmount);
        await handlePurchaseSuccess();
        return;
      }

      if (paymentGateway !== "CCBILL" || !nextPaymentForm) {
        closeAlert();
        showPurchasePaymentError("Unsupported payment gateway. Please try again.");
        return;
      }

      if (purchaseId) setPurchaseTransactionId(purchaseId);

      const responseToken = getFormFieldValue(nextPaymentForm, "paymentTokenId");
      const existingPaymentTokenId =
        responseToken && hasValidValue(responseToken) ? responseToken : await fetchSavedPaymentToken();

      closeAlert();

      if (!existingPaymentTokenId) {
        setPaymentForm(nextPaymentForm);
        setCcBillModalOpen(true);
        return;
      }

      const preparedData = {
        paymentTokenId: existingPaymentTokenId,
        clientSubacc: getFormFieldValue(nextPaymentForm, "clientSubacc") || process.env.REACT_APP_CCBILL_SUBACCOUNT,
        currencyCode: Number(getFormFieldValue(nextPaymentForm, "currencyCode")) || PAYMENT_INFO.currencyCode,
        initialPrice: Number(getFormFieldValue(nextPaymentForm, "initialPrice")) || purchasePayload.amount,
        initialPeriod: Number(getFormFieldValue(nextPaymentForm, "initialPeriod")) || PAYMENT_INFO.initialPeriod,
        passThroughInfo: buildPassThroughInfo(nextPaymentForm),
      };

      const amount = purchasePayload?.amount ?? (selectedItem && getPrice(selectedItem)) ?? 0;
      const confirmed = await confirmDialog(
        "Confirm Payment",
        `You have a saved payment method. Would you like to purchase credits for $${Number(amount).toFixed(2)}?`,
        {
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          cancelButtonColor: "#d33",
        }
      );

      if (confirmed) {
        await chargeWithExistingToken(purchaseId, preparedData);
      } else {
        if (purchaseId) await cancelCreditPurchase(purchaseId, true);
        closeAlert();
      }
    } catch (err) {
      closeAlert();
      showPurchasePaymentError(
        err?.response?.data?.message || err?.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedCreditOption,
    purchasePayload,
    isProcessing,
    fetchSavedPaymentToken,
    handlePurchaseSuccess,
    chargeWithExistingToken,
    showPurchasePaymentError,
  ]);

  const maxCredits = creditList.length
    ? Math.max(...creditList.map((o) => getCredits(o)))
    : 480;

  return (
    <>
      <Modal
        isOpen={isOpen}
        centered
        className={`credit_ptions_modal ${isLowCreditCallout ? "credit_ptions_modal_low_credit" : ""}`}
      >

        {/* Note: This is used to show the call credits modal on subscriber side */}
        {!isLowCreditCallout && (
          <>
            <div className="d-flex align-items-center justify-content-center mb-3">
              <h5 className="m-0">Call Credits</h5>
              <span className="cm_close_btn" onClick={onClose}>
                <i className="fa-solid fa-xmark"></i>
              </span>
            </div>

            {maxCredits > 0 && (
              <p style={{ textAlign: "center" }}>
                Purchase up to {maxCredits} credits per transaction
              </p>
            )}
          </>
        )}

        {/* Note: This is used to show the low credit modal on creator side */}
        {isLowCreditCallout && (
          <div className="cm_purchase_low_credit_header">
            <div className="cm_purchase_low_credit_icon_wrap" aria-hidden>
              <i className="fa-solid fa-triangle-exclamation cm_purchase_low_credit_icon" />
            </div>
            <h2 className="cm_purchase_low_credit_title">
              Keep it Going <span className="cm_purchase_low_credit_sparkle">✨</span>
            </h2>
            <p className="cm_purchase_low_credit_subtitle">
              {lowCreditSubtitle}
            </p>
          </div>
        )}

        {/* Note: This is used to show the loading state on subscriber side */}
        {loading && (
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <Loader type="Oval" color="#F334BE" height={48} width={48} />
            <p
              className={isLowCreditCallout ? "cm_purchase_low_credit_loading_text" : ""}
              style={
                isLowCreditCallout
                  ? undefined
                  : { textAlign: "center", marginTop: "1rem", marginBottom: "0" }
              }
            >
              Loading credit options...
            </p>
          </div>
        )}

        {/* Note: This is used to show the credit options on subscriber side */}
        {!loading && (
          <>
            <div
              className={`credit_ptions_wrapper ${isLowCreditCallout ? "credit_ptions_wrapper_low_credit_stack" : ""}`}
            >
              {creditList.map((item, index) => {
                const id = getItemId(item);
                const isSelected = selectedCreditOption === id;
                const isPopular = item.isPopular;
                const priceLabel = `$${getPrice(item) + PAYMENT_INFO.extraCharge}`;
                return (
                  <div
                    key={id ?? index}
                    className={`credit_options_card ${isSelected ? "selected" : ""} ${isPopular ? "cm_trending_card" : ""} ${isLowCreditCallout ? "credit_options_card_low_credit_stack" : ""}`}
                    onClick={() => setSelectedCreditOption(id)}
                  >
                    {item.isPopular && <span className="credit_option_label">Most Popular</span>}
                    {isLowCreditCallout ? (
                      <>
                        <span className="cm_purchase_low_credit_coin" aria-hidden>
                          <i className="fa-solid fa-coins" />
                        </span>
                        <span className="cm_purchase_low_credit_pkg_main">
                          <span className="cm_purchase_low_credit_credits">{getCredits(item)} Credits</span>
                        </span>
                        <span className="cm_purchase_low_credit_price">{priceLabel}</span>
                      </>
                    ) : (
                      <>
                        <p>{getCredits(item)} Credits</p>
                        <p>{priceLabel}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedItem && purchasePayload && (
              <div className="jh-modern-tip-vat-info mb-1">
                <p className="jh-modern-tip-security-text" style={{ marginBottom: '12px' }}>
                  {getVatAndServiceFeeAppliedText(purchasePayload?.vatAmount ?? 0)}
                </p>
              </div>
            )}

            {isLowCreditCallout ? (
              <div className="cm_purchase_low_credit_actions">
                <button
                  type="button"
                  className="cm_purchase_low_credit_btn_cancel"
                  disabled={isProcessing}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cm_purchase_low_credit_btn_continue"
                  disabled={!selectedCreditOption || isProcessing}
                  onClick={handleConfirmPurchase}
                >
                  {isProcessing ? "Processing..." : "Continue Call"}
                </button>
              </div>
            ) : (
              <button
                className="modern-wallet-add-card-btn"
                disabled={!selectedCreditOption || isProcessing}
                onClick={handleConfirmPurchase}
              >
                {isProcessing ? "Processing..." : "Confirm"}
              </button>
            )}

            {/* Note: This is used to show the payment footer on subscriber side */}
            <div
              id="payment-footer"
              className={`payment-footer ${isLowCreditCallout ? "cm_purchase_low_credit_footer_note" : ""}`}
            >
              {isLowCreditCallout ? (
                <p className="cm_purchase_low_credit_footer_tagline">It only takes one click to stay connected</p>
              ) : (
                <p>
                  <i className="fa-solid fa-shield-halved"></i>
                  Secure payment powered by CCBill
                </p>
              )}
            </div>
          </>
        )}
      </Modal>

      {ccBillModalOpen && paymentForm && (
        <CCBillModal
          paymentForm={paymentForm}
          openModal={ccBillModalOpen}
          toggleModal={closeCCBillModal}
          onCancel={cancelCCBillModal}
          onSuccess={handleCCBillModalSuccess}
          purchaseAmount={purchasePayload?.amount ?? (selectedItem && getPrice(selectedItem)) ?? 0}
          creatorInfo={null}
          vatAmount={purchasePayload?.vatAmount ?? 0}
          vatPercentage={purchasePayload?.vatPercentage ?? 0}
        />
      )}
    </>
  );
};

export default PurchaseCreditsModal;
